import { CheckCircle, Lightbulb, XCircle } from "lucide-react";
import { useState } from "react";
import { getTicketStatusConfig } from "../../config/statuses";
import { useRejectSolution, useValidateSolution } from "../../hooks/useTicketMutations";
import type { Ticket } from "../../types/ticket.types";
import { formatRelativeDate } from "../../utils";
import { TicketStatusBadge } from "../shared/TicketStatusBadge";

interface TicketSolutionProps {
  ticket: Ticket;
  onUpdate: () => void;
}

export function TicketSolution({ ticket, onUpdate }: TicketSolutionProps) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const validateSolution = useValidateSolution();
  const rejectSolution = useRejectSolution();

  const solutionText = ticket.solutionDescription;
  const hasSolution = !!solutionText;
  const isProposed = ticket.status === "proposition_solution";
  const isResolved = ticket.status === "resolved";
  const showSolution = isProposed || isResolved;

  if (!showSolution || !hasSolution) return null;

  const statusConfig = getTicketStatusConfig(ticket.status);

  const handleValidate = () => {
    validateSolution.mutate(ticket.id, {
      onSuccess: () => onUpdate(),
    });
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) return;
    rejectSolution.mutate(
      { id: ticket.id, data: { rejectionReason: rejectionReason.trim() } },
      {
        onSuccess: () => {
          setShowRejectForm(false);
          setRejectionReason("");
          onUpdate();
        },
      },
    );
  };

  return (
    <div
      className="card bg-base-200 border border-base-300/70 shadow-sm border-l-4 hover:shadow-md hover:border-primary/20 transition-all duration-200"
      style={{ borderLeftColor: `var(--status-${statusConfig.token})` }}
    >
      <div className="card-body space-y-4">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4" style={{ color: `var(--status-${statusConfig.token})` }} />
          <h3 className="card-title text-sm font-semibold">Solution proposée</h3>
          <TicketStatusBadge status={ticket.status} size="sm" />
        </div>

        {/* Solution content */}
        <p className="text-sm whitespace-pre-wrap">{solutionText}</p>

        {/* Resolution date */}
        {ticket.resolvedAt && (
          <div className="flex items-center gap-2 text-xs text-base-content/50">
            <span>
              Résolu{" "}
              <time dateTime={ticket.resolvedAt}>{formatRelativeDate(ticket.resolvedAt)}</time>
            </span>
          </div>
        )}

        {/* Rejection reason display */}
        {ticket.rejectionReason && (
          <div className="text-xs text-error/80 bg-error/5 rounded p-2 border border-error/20">
            <span className="font-medium">Motif du rejet :</span> {ticket.rejectionReason}
          </div>
        )}

        {/* Action buttons — ticket creator only when status = proposition_solution */}
        {isProposed && (
          <div className="flex items-center gap-2 pt-2 border-t border-base-200">
            <button
              type="button"
              className="btn btn-success btn-sm gap-1.5 active:scale-95 transition-transform"
              onClick={handleValidate}
              disabled={validateSolution.isPending}
            >
              {validateSolution.isPending ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <CheckCircle className="w-3.5 h-3.5" />
              )}
              Valider
            </button>

            {!showRejectForm ? (
              <button
                type="button"
                className="btn btn-error btn-outline btn-sm gap-1.5 active:scale-95 transition-transform"
                onClick={() => setShowRejectForm(true)}
              >
                <XCircle className="w-3.5 h-3.5" />
                Rejeter
              </button>
            ) : (
              <div className="flex-1 space-y-2">
                <textarea
                  className="textarea textarea-bordered textarea-sm w-full bg-base-200 border-base-300 focus:border-error focus:ring-2 focus:ring-error/20 transition-all duration-200 min-h-[60px]"
                  placeholder="Motif du rejet..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn btn-error btn-sm gap-1.5 active:scale-95 transition-transform"
                    onClick={handleReject}
                    disabled={!rejectionReason.trim() || rejectSolution.isPending}
                  >
                    {rejectSolution.isPending ? (
                      <span className="loading loading-spinner loading-xs" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5" />
                    )}
                    Confirmer le rejet
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm active:scale-95 transition-transform"
                    onClick={() => {
                      setShowRejectForm(false);
                      setRejectionReason("");
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
